import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/contexts/LanguageContext";

interface UpsellOption {
  id: string;
  name: string;
  price: number;
  duration: number;
  isNew?: boolean;
}

const UpsellForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { t } = useLanguage();
  const isEditing = !!id;

  const [name, setName] = useState("");
  const [required, setRequired] = useState(false);
  const [selectionMode, setSelectionMode] = useState<"single" | "multiple">("single");
  const [options, setOptions] = useState<UpsellOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);

  useEffect(() => {
    if (!isEditing) return;
    const fetch = async () => {
      const { data } = await supabase
        .from("upsells")
        .select("*, upsell_options(*)")
        .eq("id", id)
        .maybeSingle();
      if (data) {
        setName(data.name);
        setRequired(data.required);
        setSelectionMode((data.option_selection_mode as any) || "single");
        setOptions(
          ((data as any).upsell_options || []).map((o: any) => ({
            id: o.id,
            name: o.name,
            price: Number(o.price),
            duration: o.duration,
          }))
        );
      }
      setFetching(false);
    };
    fetch();
  }, [id, isEditing]);

  const addOption = () => {
    setOptions([...options, { id: crypto.randomUUID(), name: "", price: 0, duration: 0, isNew: true }]);
  };

  const updateOption = (optId: string, field: keyof UpsellOption, value: string | number) => {
    setOptions(options.map((o) => (o.id === optId ? { ...o, [field]: value } : o)));
  };

  const removeOption = (optId: string) => {
    setOptions(options.filter((o) => o.id !== optId));
  };

  const handleSave = async () => {
    if (!name || !profile?.organisation_id) {
      toast.error(t("upsells.fill_required"));
      return;
    }
    setLoading(true);
    try {
      let upsellId = id;
      if (isEditing) {
        const { error } = await supabase
          .from("upsells")
          .update({ name, required, option_selection_mode: selectionMode })
          .eq("id", id!);
        if (error) throw error;
        await supabase.from("upsell_options").delete().eq("upsell_id", id!);
      } else {
        const { data, error } = await supabase
          .from("upsells")
          .insert({
            organisation_id: profile.organisation_id,
            name,
            required,
            option_selection_mode: selectionMode,
          })
          .select()
          .single();
        if (error) throw error;
        upsellId = data.id;
      }

      if (options.length > 0 && upsellId) {
        const { error } = await supabase.from("upsell_options").insert(
          options.map((o) => ({
            upsell_id: upsellId!,
            name: o.name,
            price: o.price,
            duration: o.duration,
          }))
        );
        if (error) throw error;
      }

      toast.success(isEditing ? t("upsells.updated") : t("upsells.created"));
      navigate("/dashboard/upsells");
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <Button variant="ghost" size="sm" asChild className="mb-4 -ml-2 text-muted-foreground">
        <Link to="/dashboard/upsells">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t("upsells.back")}
        </Link>
      </Button>

      <h1 className="text-xl font-bold text-foreground mb-6">
        {isEditing ? t("upsells.edit_title") : t("upsells.create_title")}
      </h1>

      {/* Name & Settings */}
      <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
        <h2 className="text-base font-semibold text-foreground mb-4">{t("upsells.settings")}</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2 md:col-span-2">
            <Label>{t("upsells.name_label")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("upsells.name_placeholder")} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{t("upsells.selection_mode")}</Label>
            <Select value={selectionMode} onValueChange={(v: "single" | "multiple") => setSelectionMode(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="single">{t("upsells.mode_single")}</SelectItem>
                <SelectItem value="multiple">{t("upsells.mode_multiple")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 md:pt-5">
            <Switch checked={required} onCheckedChange={setRequired} />
            <div>
              <Label className="cursor-pointer text-sm" onClick={() => setRequired(!required)}>{t("upsells.required")}</Label>
              <p className="text-[11px] text-muted-foreground leading-tight">
                {required ? t("upsells.required_desc") : t("upsells.optional_desc")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Options */}
      <section className="rounded-xl border border-border bg-card p-6 shadow-card mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-foreground">{t("upsells.options_title")}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">{t("upsells.options_desc")}</p>
          </div>
          <Button variant="outline" size="sm" onClick={addOption}>
            <Plus className="h-4 w-4 mr-1" />
            {t("upsells.add_option")}
          </Button>
        </div>

        {options.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border py-8 text-center">
            <p className="text-sm text-muted-foreground">{t("upsells.no_options")}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {options.map((opt, idx) => (
              <div key={opt.id} className="rounded-xl border border-border p-4 bg-muted/30">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-muted-foreground">{t("upsells.option_label")} {idx + 1}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => removeOption(opt.id)}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("upsells.option_name")}</Label>
                    <Input value={opt.name} onChange={(e) => updateOption(opt.id, "name", e.target.value)} placeholder={t("upsells.option_name_placeholder")} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("upsells.option_price")}</Label>
                    <Input type="number" min="0" step="0.01" value={opt.price || ""} onChange={(e) => updateOption(opt.id, "price", parseFloat(e.target.value) || 0)} placeholder="0.00" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("upsells.option_duration")}</Label>
                    <Input type="number" min="0" step="5" value={opt.duration || ""} onChange={(e) => updateOption(opt.id, "duration", parseInt(e.target.value) || 0)} placeholder="0" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={!name || loading}>
          {loading ? t("upsells.saving") : isEditing ? t("upsells.save_changes") : t("upsells.create")}
        </Button>
        <Button variant="ghost" asChild>
          <Link to="/dashboard/upsells">{t("upsells.cancel")}</Link>
        </Button>
      </div>
    </div>
  );
};

export default UpsellForm;
